import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = {
  direccion: "", ciudad: "", estado: "", cp: "",
  tipo_uso: "habitacional", estado_registral: "", folio_real: "",
  superficie_terreno: "", superficie_construida: "", descripcion: "",
};

// Module-scope stable component. Defining it inside the parent would remount
// the underlying <input> on every keystroke and steal focus.
function Field({ label, name, options, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      {options ? (
        <Select value={value || ""} onValueChange={(v) => onChange(name, v)}>
          <SelectTrigger data-testid={`inmueble-${name}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(name, e.target.value)} data-testid={`inmueble-${name}`}
               className="w-full h-11 px-3 rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
      )}
    </div>
  );
}

export default function InmuebleFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.direccion.trim()) return toast.error("Ingresa la dirección");
    setSaving(true);
    try {
      const url = initial?.id ? `/inmuebles/${initial.id}` : "/inmuebles";
      const method = initial?.id ? "put" : "post";
      const { data } = await api[method](url, f);
      toast.success(initial?.id ? "Inmueble actualizado" : "Inmueble creado");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="inmueble-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Editar inmueble" : "Nuevo inmueble"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Dirección completa" name="direccion" value={f.direccion} onChange={set} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Ciudad" name="ciudad" value={f.ciudad} onChange={set} />
            <Field label="Estado" name="estado" value={f.estado} onChange={set} />
            <Field label="C.P." name="cp" value={f.cp} onChange={set} />
          </div>
          <Field label="Tipo de uso" name="tipo_uso" value={f.tipo_uso} onChange={set} options={[
            { value: "habitacional", label: "Habitacional" },
            { value: "comercial", label: "Comercial" },
            { value: "oficina", label: "Oficina" },
            { value: "bodega", label: "Bodega" },
            { value: "predio", label: "Predio" },
            { value: "terreno", label: "Terreno" },
            { value: "mixto", label: "Mixto" },
          ]} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Folio real" name="folio_real" value={f.folio_real} onChange={set} />
            <Field label="Estado registral" name="estado_registral" value={f.estado_registral} onChange={set} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Superficie terreno (m²)" name="superficie_terreno" value={f.superficie_terreno} onChange={set} type="number" />
            <Field label="Superficie construida (m²)" name="superficie_construida" value={f.superficie_construida} onChange={set} type="number" />
          </div>
          <Field label="Descripción / notas" name="descripcion" value={f.descripcion} onChange={set} />
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="text-slate-600 px-4 py-2" data-testid="inmueble-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="bm-btn-primary" data-testid="inmueble-save">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
